// The admin dashboard's confirm dialect — a skin over the shared @auctogon/ui
// confirm primitive (BL-1246, ADR 030 § 3b Canon merge; admin's icon-led layout
// was the canonical base the shared core was built from).
//
// The contract (await `onConfirm`, close only on resolve, hold open with the
// error inline on reject) and the layout now live in the shared core; this file
// binds the admin appearance and supplies the modal wrapper. The public prop
// signature is UNCHANGED, so all 44 admin call sites migrate with no edit.
//
// The modal wrapper is the shared admin `Modal` (BL-2624, BL-1710 slice 2),
// through its bare path: no chrome props, so the confirm content below is the
// whole panel. A closed confirm renders nothing, so it leaves the DOM rather
// than sitting there as an inert ghost the way it did on the old eager-mount primitive.
// The shared core renders no modal precisely so this one line could change
// without touching any of the call sites.
//
// TWO BEHAVIOURAL FIXES ARRIVE WITH THE MERGE, both inherited from the core
// (which took bidder's half on each — see the core's file header):
//   - A failed confirm no longer leaves its error on screen the NEXT time the
//     same dialog is opened. Admin kept `error` in state across a close.
//   - Escape and the backdrop are both gated while a confirm is in flight.
//     Admin gated only the backdrop, so an Escape mid-request dropped the
//     pending rejection and silently lost the failure.
// The rendered DOM is otherwise unchanged, with one addition: the inline error
// now carries `role="alert"`, so a screen reader announces a failure the user
// did not scroll to. No pixels move.
'use client';

import { useId, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { ConfirmDialogContent, FIELD_LABEL_LOOK, useConfirmAction, type ConfirmDialogDialect } from '@auctogon/ui/web';
import Modal from './Modal';
import { ADMIN_MODAL_SIZES, type AdminModalSize } from './modal-sizes';
import Button from './Button';
import TextInput from './TextInput';

/**
 * Admin appearance — the classic dashboard dialect. Every class here is lifted
 * verbatim from the pre-merge component, so the merge moves no pixels.
 */
const ADMIN_CONFIRM_DIALECT: ConfirmDialogDialect = {
    // `max-h` + a scrolling MESSAGE (not a scrolling panel): the dialect's half
    // of BUG-1620, and deliberately the same shape `Modal`'s dialect already
    // uses — ceiling on the panel, `flex flex-col` so the height distributes,
    // `overflow-y-auto min-h-0` on the one region that may shrink. The ceiling
    // is `--modal-max-h`, the SAME token `.ui-modal` and `Modal` read: one
    // number, one definition (settings-v2.css).
    //
    // Width is composed on at the call site from the shared size map, so it is
    // absent here. Everything else is lifted verbatim from the pre-BUG-1620
    // dialect, so a dialog that already fitted does not move a pixel.
    // No horizontal margin: `Modal`'s centring layer carries the 16px gutter.
    root: 'bg-white rounded-xl shadow-xl w-full p-6 flex flex-col max-h-[var(--modal-max-h)]',
    // `items-stretch`, not `items-start`: the text column has to inherit the
    // row's (now bounded) height before anything inside it can scroll. The
    // glyph is unaffected — its explicit `h-10` beats the stretch — and the
    // column's own contents stay top-aligned because it is a `flex-col` with
    // the default `justify-start`. So this moves nothing on a short dialog.
    // `min-h-0` is what lets the row shrink below its content under the ceiling.
    header: 'flex items-stretch gap-4 min-h-0',
    glyph: {
        danger: 'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-[var(--danger-100)]',
        default: 'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100',
    },
    icon: {
        danger: 'w-5 h-5 text-[var(--danger-500)]',
        default: 'w-5 h-5 text-amber-600',
    },
    body: 'flex-1 min-w-0 flex flex-col min-h-0',
    title: 'text-title font-semibold text-gray-900 shrink-0',
    message: 'mt-2 text-body text-gray-600 overflow-y-auto min-h-0',
    // `shrink-0` on both: under the ceiling, flex distributes the deficit across
    // every shrinkable child. Without it the BUTTON ROW is squeezed away first —
    // which is the original bug wearing a different hat.
    error: 'mt-3 text-body text-[var(--danger-700)] bg-[var(--danger-50)] rounded-lg px-3 py-2 shrink-0',
    footer: 'flex flex-wrap justify-end gap-3 mt-6 shrink-0',
};

/**
 * Props for the ConfirmDialog component.
 */
export interface ConfirmDialogProps {
    /**
     * Test-ID namespace for automated checks. Yields `<testId>` on the dialog,
     * `-confirm` / `-cancel` on the two actions, and `-error` on the inline
     * refusal a rejected `onConfirm` leaves open (BL-1874).
     *
     * No default: 43 of this repo's 45 call sites pass nothing, and seven
     * screens mount two or more ConfirmDialogs at once, so a shared default
     * would tag all of them identically at the same time. Name one when you
     * need to target it.
     *
     * A closed ConfirmDialog renders nothing (it is built on `Modal`), so a
     * present `[data-testid="<testId>"]` is an open one. It emits no `data-state`.
     */
    testId?: string;
    /** Whether the dialog is visible */
    isOpen: boolean;
    /** Callback to close the dialog */
    onClose: () => void;
    /** Callback when user confirms — may be async */
    onConfirm: () => void | Promise<void>;
    /** Dialog heading */
    title: string;
    /** Descriptive body text or content */
    message: string | React.ReactNode;
    /** Text for the confirm button (default: "Confirm") */
    confirmText?: string;
    /** Text for the cancel button (default: "Cancel") */
    cancelText?: string;
    /**
     * A choice of its own on the cancel button (BL-2648: the settlement
     * checklist's Keep Preparing saves the tick without opening). Awaited like
     * `onConfirm`: resolve → `onClose`; throw → the message stays inline. Escape
     * and the backdrop still only call `onClose`, so they never make the choice.
     * Unset, the cancel button is `onClose`, as before.
     */
    onCancel?: () => void | Promise<void>;
    /** Visual style — danger shows red confirm button (default: "default") */
    variant?: 'default' | 'danger';
    /** Externally controlled loading state (overrides internal tracking) */
    isLoading?: boolean;
    /** z-index for the modal overlay (default: 50, use higher to stack above other modals) */
    zIndex?: number;
    /** Optional custom class for the modal container (forwarded to Modal) */
    containerClassName?: string;
    /**
     * Panel width, from the shared admin modal scale (BUG-1620). Defaults to
     * `md` — the width all 45 call sites had before the prop existed — so this
     * is opt-in and no existing dialog changes.
     *
     * Reach for it when the dialog's `message` is a rendered ARTEFACT rather
     * than a sentence: the date-change confirmation embeds a full email preview,
     * which at `md` left a 344px column that broke each date over several lines.
     *
     * This is a VARIANT naming which surface this is, not a domain value
     * (rule #36 / ADR 029 Cluster 3) — no source owns a competing answer to
     * "how wide should this dialog be".
     */
    size?: AdminModalSize;
    /**
     * Disable Confirm while the dialog's own body is incomplete (BL-1289) — for
     * a dialog whose `message` composes in an input the operator must fill, e.g.
     * the auction restore stage picker.
     *
     * This is TRANSIENT UI STATE, not a domain value (rule #36 / ADR 029): it
     * describes this render, not a fact about the record, so no source owns a
     * competing answer. Composes with `requireTypedConfirmation` — either one
     * being unsatisfied keeps Confirm disabled.
     */
    confirmDisabled?: boolean;
    /**
     * Gate Confirm behind an exact typed phrase (BL-1238). Use for irreversible
     * bulk actions where a single misplaced click is unacceptable — the operator
     * has to reproduce `phrase` character-for-character before Confirm enables.
     * The match is case- and whitespace-sensitive on purpose: the friction IS the
     * feature. Omit for ordinary confirmations.
     *
     * Lives in this SKIN, not the shared core: the core's `message` is a
     * `ReactNode`, so the input composes in from here without widening a
     * cross-app primitive (and without touching bidder-web) for what is so far
     * one admin call site.
     */
    requireTypedConfirmation?: {
        /** The exact string the operator must type. */
        phrase: string;
        /** Optional override for the input's label (default: `Type <phrase> to confirm`). */
        label?: string;
    };
}

/**
 * The confirm button's action and, when the caller gives one, the cancel
 * button's own (BL-2648), each on the shared await-then-close contract.
 * Either in flight holds both buttons and both dismiss paths, so a late
 * refusal always lands on a dialog the operator can still see.
 *
 * @param input - The dialog's open state, `onClose`, `onConfirm`, optional `onCancel` and external loading flag.
 * @returns Whether anything is in flight, whether the confirm is, the inline error, and both click handlers.
 */
function useDialogActions(input: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    isLoading?: boolean;
}) {
    const { isOpen, onClose, onConfirm, onCancel, isLoading } = input;
    const confirmAction = useConfirmAction({ isOpen, onClose, onConfirm, isLoading });
    const cancelAction = useConfirmAction({ isOpen, onClose, onConfirm: onCancel ?? onClose });
    // Only the button pressed last speaks: a refusal the other button left behind is stale.
    const [lastActed, setLastActed] = useState<'confirm' | 'cancel'>('confirm');
    return {
        isPending: confirmAction.isPending || (onCancel != null && cancelAction.isPending),
        confirmPending: confirmAction.isPending,
        error: lastActed === 'cancel' ? cancelAction.error : confirmAction.error,
        confirm: () => {
            setLastActed('confirm');
            confirmAction.confirm();
        },
        cancel: onCancel != null
            ? () => {
                setLastActed('cancel');
                cancelAction.confirm();
            }
            : onClose,
    };
}

/**
 * A standardized confirmation dialog for destructive and important actions.
 *
 * Handles async confirm callbacks, loading states, and inline error display —
 * all owned by the shared core, which this component skins.
 *
 * @param props - Dialog configuration including callbacks and variant.
 * @returns A modal with icon, message, and cancel/confirm buttons.
 */
export default function ConfirmDialog({
    testId,
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onCancel,
    variant = 'default',
    isLoading: externalLoading,
    zIndex,
    containerClassName,
    size = 'md',
    confirmDisabled = false,
    requireTypedConfirmation,
}: ConfirmDialogProps) {
    const { isPending, confirmPending, error, confirm, cancel } = useDialogActions({
        isOpen,
        onClose,
        onConfirm,
        onCancel,
        isLoading: externalLoading,
    });
    const [typedConfirmation, setTypedConfirmation] = useState('');
    const confirmationInputId = useId();

    // Re-lock the gate on every open. Without this, a phrase typed for one
    // destructive action would still be satisfied the next time the dialog
    // opens, letting the next irreversible delete through with no friction.
    // Adjusted during render (React's documented "reset state when a prop
    // changes" pattern) rather than in an effect, so the input is already
    // cleared on the paint that reveals it — an effect would render one frame
    // with the stale phrase, and Confirm enabled, before clearing.
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (isOpen) setTypedConfirmation('');
    }

    const confirmationSatisfied =
        !requireTypedConfirmation || typedConfirmation === requireTypedConfirmation.phrase;

    // Composed into the core's `message` slot so the core stays untouched.
    const body = requireTypedConfirmation ? (
        <div className="space-y-4">
            {message}
            <div>
                <label htmlFor={confirmationInputId} className={FIELD_LABEL_LOOK}>
                    {requireTypedConfirmation.label ?? `Type ${requireTypedConfirmation.phrase} to confirm`}
                </label>
                <div className="mt-1">
                    <TextInput
                        id={confirmationInputId}
                        value={typedConfirmation}
                        onChange={setTypedConfirmation}
                        disabled={isPending}
                        placeholder={requireTypedConfirmation.phrase}
                    />
                </div>
            </div>
        </div>
    ) : (
        message
    );

    return (
        <Modal
            testId={testId}
            isOpen={isOpen}
            onClose={onClose}
            // Names the dialog for assistive tech and role queries. The old
            // eager wrapper left it unnamed; the title is already a string.
            ariaLabel={title}
            // Gate BOTH dismiss paths while the confirm is in flight. Escape is
            // the one that bites: `Modal` defaults `closeOnEscape` to true and
            // listens for it on the document, so leaving
            // it unpassed means Escape closes the dialog mid-request and the
            // later rejection lands on a dialog the operator can no longer see —
            // the failure is silently lost. That was the pre-merge behaviour;
            // gating it here is half of what makes the close-only-on-confirmed-
            // success contract actually hold on this side.
            closeOnBackdropClick={!isPending}
            closeOnEscape={!isPending}
            zIndex={zIndex}
            containerClassName={containerClassName}
        >
            <ConfirmDialogContent
                // Composed onto the dialect root rather than stated in it: the
                // shared core has no `size` slot of its own (it renders no
                // modal), and `className` is the seam it already provides.
                className={ADMIN_MODAL_SIZES[size]}
                title={title}
                message={body}
                error={error}
                icon={AlertTriangle}
                variant={variant}
                dialect={ADMIN_CONFIRM_DIALECT}
                testId={testId}
                actions={
                    <>
                        <Button
                            variant="outline"
                            onClick={cancel}
                            disabled={isPending}
                            data-testid={testId ? `${testId}-cancel` : undefined}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            variant={variant === 'danger' ? 'destructive' : 'primary'}
                            onClick={confirm}
                            data-testid={testId ? `${testId}-confirm` : undefined}
                            isLoading={confirmPending}
                            disabled={!confirmationSatisfied || confirmDisabled || isPending}
                        >
                            {confirmText}
                        </Button>
                    </>
                }
            />
        </Modal>
    );
}
